import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/apiClient';
import { ConsolidatedRecord } from '../types';

type ApiConsolidatedPayload = {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  email?: string;
  invoiceId?: number;
  userId?: number;
  amount?: string;
  description?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  transactionId?: string;
  transactionStatus?: string;
};

type ApiConsolidatedMeta = {
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
};

export type ConsolidatedResponse = {
  data: ConsolidatedRecord[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
};

const extractConsolidatedFromResponse = (payload: unknown): ApiConsolidatedPayload[] => {
  if (Array.isArray(payload)) return payload as ApiConsolidatedPayload[];

  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: ApiConsolidatedPayload[] }).data;
  }

  return [];
};

const mapConsolidated = (item: ApiConsolidatedPayload, index: number): ConsolidatedRecord => ({
  id: item.invoiceId ?? index + 1,
  firstName: item.firstName ?? 'Unknown',
  lastName: item.lastName ?? 'Unknown',
  phoneNumber: item.phoneNumber ?? '--',
  email: item.email ?? 'unknown@example.com',
  invoiceId: item.invoiceId ?? 0,
  userId: item.userId ?? 0,
  amount: item.amount ?? '0.00',
  description: item.description ?? 'No description provided.',
  status: item.status ?? 'UNKNOWN',
  createdAt: item.createdAt ?? '',
  updatedAt: item.updatedAt ?? '',
  transactionId: item.transactionId ?? '--',
  transactionStatus: item.transactionStatus ?? 'UNKNOWN',
});

const extractMetaFromResponse = (payload: unknown, fallbackCount: number): ConsolidatedResponse['meta'] => {
  if (payload && typeof payload === 'object' && (payload as { meta?: unknown }).meta) {
    const rawMeta = (payload as { meta?: ApiConsolidatedMeta }).meta ?? {};
    return {
      total: rawMeta.total ?? fallbackCount,
      page: rawMeta.page ?? 1,
      limit: rawMeta.limit ?? fallbackCount,
      totalPages: rawMeta.totalPages ?? 1,
    };
  }

  return {
    total: fallbackCount,
    page: 1,
    limit: fallbackCount,
    totalPages: 1,
  };
};

const fetchConsolidatedTransactions = async (
  page: number,
  limit: number,
  statusFilter: string,
  sortField: string,
  sortDirection: 'asc' | 'desc',
  searchTerm: string
): Promise<ConsolidatedResponse> => {
  const normalizedStatus = statusFilter.trim().toUpperCase();
  const statusParam =
    normalizedStatus && normalizedStatus !== 'ALL'
      ? `&status=${encodeURIComponent(normalizedStatus.toLowerCase())}`
      : '';
  const sortParam = sortField.trim().length > 0 ? `&sortBy=${encodeURIComponent(sortField)}` : '';
  const sortDirectionParam = sortField.trim().length > 0 ? `&sortDirection=${encodeURIComponent(sortDirection)}` : '';
  const searchParam = searchTerm.trim().length > 0 ? `&search=${encodeURIComponent(searchTerm.trim())}` : '';
  const responseData = await apiClient.getJson<unknown>(
    `/transactions/consolidated?page=${page}&limit=${limit}${statusParam}${sortParam}${sortDirectionParam}${searchParam}`
  );
  const mappedData = extractConsolidatedFromResponse(responseData).map(mapConsolidated);
  const meta = extractMetaFromResponse(responseData, mappedData.length);

  return {
    data: mappedData,
    meta,
  };
};

export const useConsolidatedTransactionsQuery = (
  enabled: boolean,
  page: number,
  limit: number,
  statusFilter: string,
  sortField: string,
  sortDirection: 'asc' | 'desc',
  searchTerm: string
) =>
  useQuery({
    queryKey: ['transactions', 'consolidated', page, limit, statusFilter, sortField, sortDirection, searchTerm],
    queryFn: () => fetchConsolidatedTransactions(page, limit, statusFilter, sortField, sortDirection, searchTerm),
    enabled,
  });
